// src/hooks/usePrintFrame.ts
/**
 * Print a page whose charts size themselves from their container.
 *
 * recharts' ResponsiveContainer measures with a ResizeObserver, which does not
 * run between "print" being pressed and the page being laid out for paper — so
 * charts printed at their screen width and ran off the sheet. `start` first
 * narrows the content to the printable width, waits for the charts to redraw,
 * then opens the print dialog; `framing` is true for that whole time.
 *
 * @module hooks/usePrintFrame
 */
import { useEffect, useState } from 'react';

/** A4 landscape (297mm) less 10mm margins each side, in CSS px. */
export const PRINT_FRAME_WIDTH_PX = 1040;
/** Long enough for a ResizeObserver pass and a chart redraw. */
const SETTLE_MS = 400;

export const usePrintFrame = (): { framing: boolean; start: () => void } => {
  const [framing, setFraming] = useState(false);

  useEffect(() => {
    if (!framing) return;
    const done = () => setFraming(false);
    window.addEventListener('afterprint', done);
    const timer = window.setTimeout(() => {
      window.print();
      // Some browsers return from print() at once and fire afterprint late or never.
      window.setTimeout(done, SETTLE_MS);
    }, SETTLE_MS);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('afterprint', done);
    };
  }, [framing]);

  return { framing, start: () => setFraming(true) };
};
